// Section 15: death. Every death produces a report line with name, age, cause and the three
// most relevant contributing factors; triggers grief; leaves the job slot empty (never
// auto-filled); reports knowledge lost (Section 11); updates the Ledger (Section 21).
import { ageGroupOf, hasBuilding, peopleSorted } from '../../core/queries';
import type {
  DeathCause,
  Factor,
  IllnessId,
  Person,
  PersonId,
  PhaseContext,
  SkillId,
} from '../../core/types';
import { remember } from './memory';
import { num } from './value';

const MEMORY_LABELS: Partial<Record<Person['memory'][number]['kind'], string>> = {
  'fed-short': 'fed short',
  'fed-none': 'fed nothing',
  thirsty: 'short of water',
  cold: 'short of firewood',
  'no-shelter': 'no shelter',
  'fell-ill': 'fell ill',
  injured: 'injured',
  'passed-over': 'passed over for treatment',
  punished: 'punished',
  victim: 'attacked',
};

/** Section 15: the memories and health losses that bear on each cause, listed first (Stream D). */
const RELEVANT: Partial<Record<DeathCause, readonly string[]>> = {
  starvation: ['memory:fed-short', 'memory:fed-none', 'health:hunger'],
  dehydration: ['memory:thirsty', 'health:thirst'],
  exposure: ['memory:no-shelter', 'memory:cold', 'health:exposure', 'health:cold'],
  illness: ['memory:fell-ill', 'memory:passed-over', 'health:illness'],
  injury: ['memory:injured', 'health:illness'],
  'old-age': ['age:years', 'health:age'],
  violence: ['memory:victim'],
};

/** Section 4: the season a turn fell in (turn 1 is the first Spring). */
function isWinterTurn(ctx: PhaseContext, turn: number): boolean {
  const order = ctx.cfg.seasons.order;
  return order[(((turn - 1) % order.length) + order.length) % order.length] === 'winter';
}

/**
 * The three most relevant contributing factors from memory (last 4 seasons), this season's health
 * losses and, for old age, the age itself: rows that bear on `cause` first, then by size
 * ("fed short 3 of the last 4 seasons; no shelter in Winter", Section 15).
 */
export function contributingFactors(ctx: PhaseContext, p: Person, cause?: DeathCause): Factor[] {
  const window = ctx.season.turn - 3;
  const counts = new Map<string, { n: number; winter: boolean }>();
  for (const m of p.memory) {
    const label = MEMORY_LABELS[m.kind];
    if (label === undefined || m.turn < window) continue;
    const row = counts.get(m.kind) ?? { n: 0, winter: false };
    row.n += 1;
    row.winter ||= isWinterTurn(ctx, m.turn);
    counts.set(m.kind, row);
  }
  const rows: Factor[] = [];
  for (const [kind, { n, winter }] of counts) {
    const label = MEMORY_LABELS[kind as keyof typeof MEMORY_LABELS] ?? kind;
    rows.push({
      source: `memory:${kind}`,
      op: 'add',
      value: n,
      delta: n,
      note:
        kind === 'no-shelter' && winter
          ? `${label} in Winter`
          : `${label} ${n} of the last 4 seasons`,
    });
  }
  const losses = ctx.draft.currentReport?.healthLoss[p.id] ?? {};
  for (const [lossCause, amount] of Object.entries(losses)) {
    if (amount > 0)
      rows.push({
        source: `health:${lossCause}`,
        op: 'add',
        value: -amount,
        delta: -amount,
        note: `health -${Math.round(amount)} from ${lossCause}`,
      });
  }
  if (cause === 'old-age') {
    const years = Math.floor(p.ageSeasons / ctx.cfg.people.seasonsPerYear);
    rows.push({
      source: 'age:years',
      op: 'add',
      value: years,
      delta: years,
      note: `aged ${years}`,
    });
  }
  const relevant = cause === undefined ? [] : (RELEVANT[cause] ?? []);
  const rank = (f: Factor): number => (relevant.includes(f.source) ? 0 : 1);
  return rows
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        Math.abs(b.delta) - Math.abs(a.delta) ||
        (a.source < b.source ? -1 : 1),
    )
    .slice(0, 3);
}

/** Section 9 and 18: cemetery burial (grief halved, death costs -1 happiness instead of -3). */
function cemeteryBurial(ctx: PhaseContext): boolean {
  return hasBuilding(ctx.draft, 'cemetery') && ctx.draft.policies.burial === 'cemetery';
}

function reportKnowledgeLost(ctx: PhaseContext, p: Person): void {
  const s = ctx.cfg.skills;
  const others = peopleSorted(ctx.draft).filter((q) => q.id !== p.id);
  for (const [skill, level] of Object.entries(p.skills) as [SkillId, number][]) {
    if (level < s.knowledgeLostMinSkill) continue;
    if (others.some((q) => (q.skills[skill] ?? 0) >= s.knowledgeHolderMinSkill)) continue;
    ctx.report.add({ kind: 'knowledge-lost', skill, personId: p.id, level });
  }
}

function grieve(ctx: PhaseContext, p: Person): void {
  const l = ctx.cfg.lifecycle;
  const seasons = Math.ceil(
    l.griefSeasons * (cemeteryBurial(ctx) ? ctx.cfg.buildings.cemetery.griefMul : 1),
  );
  const kin = [p.partnerId, ...p.childIds, ...p.parentIds].filter(
    (id): id is PersonId => id !== null,
  );
  for (const id of kin) {
    const k = ctx.draft.people[id];
    if (k === undefined) continue;
    k.status.grieving = { seasonsRemaining: seasons };
    remember(ctx, k, { turn: ctx.season.turn, kind: 'bereaved', otherId: p.id });
  }
}

function detach(ctx: PhaseContext, p: Person): void {
  const d = ctx.draft;
  const h = d.households[p.householdId];
  if (h !== undefined) {
    h.memberIds = h.memberIds.filter((id) => id !== p.id);
    if (h.memberIds.length === 0) delete d.households[h.id];
  }
  if (p.partnerId !== null) {
    const partner = d.people[p.partnerId];
    if (partner !== undefined) partner.partnerId = null;
  }
  for (const q of peopleSorted(d)) if (q.masterId === p.id) q.masterId = null;
  delete d.plantedPlots[p.id];
  delete d.toolOverrides[p.id];
  delete d.triageOverrides[p.id];
  for (const overrides of Object.values(d.rationOverrides)) delete overrides.people[p.id];
  d.myHousehold = d.myHousehold.filter((id) => id !== p.id);
}

/** Section 16: deaths cost settlement happiness; deprivation deaths also cost trust. */
function mourn(ctx: PhaseContext, p: Person, cause: DeathCause): void {
  const l = ctx.cfg.lifecycle;
  const hit = cemeteryBurial(ctx) ? l.deathHappinessCemetery : l.deathHappinessNoCemetery;
  const deprivation = cause === 'starvation' || cause === 'exposure' || cause === 'dehydration';
  for (const q of peopleSorted(ctx.draft)) {
    q.happiness = Math.max(0, q.happiness + hit);
    if (!deprivation) continue;
    const cause2 =
      q.householdId === p.householdId ? 'deprivation-death-household' : 'deprivation-death';
    q.trust = Math.min(
      ctx.cfg.people.statMax,
      Math.max(0, q.trust + num(ctx, { kind: 'trustChange', personId: q.id, cause: cause2 })),
    );
  }
}

export interface DeathOptions {
  factors?: Factor[];
  illnessId?: IllnessId;
}

/** Remove a person from the settlement as dead. In projection mode deaths are only counted. */
export function killPerson(
  ctx: PhaseContext,
  id: PersonId,
  cause: DeathCause,
  opts: DeathOptions = {},
): void {
  const p = ctx.draft.people[id];
  if (p === undefined) return;
  const report = ctx.report.current;
  if (ctx.mode === 'projection') {
    report.expectedDeaths += 1;
    return;
  }
  const factors = opts.factors ?? contributingFactors(ctx, p, cause);
  const entry: Parameters<typeof ctx.report.add>[0] = {
    kind: 'death',
    personId: id,
    name: p.name,
    ageSeasons: p.ageSeasons,
    cause,
    jobId: p.job,
    factors,
  };
  if (opts.illnessId !== undefined) entry.illnessId = opts.illnessId;
  ctx.report.add(entry);
  report.population.deaths += 1;
  if (p.job !== 'rest')
    ctx.report.add({ kind: 'job-vacant', jobId: p.job, personId: id, reason: 'died' });
  reportKnowledgeLost(ctx, p);
  grieve(ctx, p);
  detach(ctx, p);
  const ageGroup = ageGroupOf(ctx.cfg, p.ageSeasons);
  ctx.draft.formerPeople[id] = {
    id,
    name: p.name,
    sex: p.sex,
    ageSeasons: p.ageSeasons,
    ageGroup,
    origin: p.origin,
    job: p.job,
    householdId: p.householdId,
    partnerId: p.partnerId,
    parentIds: p.parentIds,
    childIds: p.childIds,
    turn: ctx.season.turn,
    fate: { kind: 'died', cause },
  };
  // The Ledger's death row comes from the `death` entry and formerPeople (one writer, ADR-0015).
  delete ctx.draft.people[id];
  mourn(ctx, p, cause);
}
